/* eslint-disable @repo/internal/dom-events/no-unsafe-event-listeners */
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type MutableRefObject,
	type RefObject,
} from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	buildAdjacency,
	createDynState,
	integrate,
	type Adjacency,
	type DynamicsCfg,
	type DynStateMap,
} from './dynamics';
import { computeIntroSchedule, easeOutBack, type IntroCfg, type IntroSchedule } from './intro';
import {
	HERO_ASSETS,
	REFERENCE_HEIGHT,
	REFERENCE_WIDTH,
	type HeroAsset,
	type IconEdge,
} from './layout';
import {
	DEFAULT_MOTION_ORIGIN,
	clamp01,
	computeAmbient,
	computeAssetParams,
	easeOutCubic,
	pxToViewBox,
	type MotionOrigin,
	type ReferenceSize,
	type StageRect,
} from './motion';
import {
	easeInOutSine,
	nextPoissonInterval,
	pickRandomEdgeId,
	type ShimmerCfg,
	type ShimmerSlot,
} from './shimmer';

export type HeroMotionOptions = {
	ambientAmp: number;
	dynamics: DynamicsCfg;
	flick: number;
	intro: IntroCfg;
	edgeOpacity: number;
	shimmer: ShimmerCfg;
	interactive?: boolean;
	motionFrozen?: boolean;
	onIntroComplete?: () => void;
};

export type EdgeBinding = {
	el: SVGLineElement;
	edgeId: string;
	from: string;
	to: string;
	baseX1: number;
	baseY1: number;
	baseX2: number;
	baseY2: number;
	liveX1: number;
	liveY1: number;
	liveX2: number;
	liveY2: number;
};

/**
 * Refs the host component owns and passes down. The host renders the
 * JSX that the refs attach to (`<div ref={stageRef}>`, `<svg>` group,
 * per-icon `<img>`s, per-edge `<line>`s), so it owns the ref objects;
 * the hook only reads/writes their `.current`.
 */
export type HeroMotionRefs = {
	stage: RefObject<HTMLDivElement | null>;
	icons: MutableRefObject<Map<string, HTMLElement>>;
	edges: MutableRefObject<Map<string, EdgeBinding>>;
	shimmerSlots: MutableRefObject<(SVGLineElement | null)[]>;
	shimmerGrads: MutableRefObject<(SVGLinearGradientElement | null)[]>;
};

export type HeroMotionHandle = {
	replayIntro: () => void;
};

/** Max number of pointer samples kept for flick-velocity estimation. */
const FLICK_SAMPLE_LIMIT = 6;
/** Window (ms) over which to compute release velocity. */
const FLICK_WINDOW_MS = 80;
/** Hard cap on flick velocity (px/s) so violent flicks don't shoot icons off-stage. */
const FLICK_MAX_SPEED = 4000;

type DragSample = { t: number; x: number; y: number };
type DragCtx = {
	id: string;
	/** Pointer position at the start of the drag, in stage-relative px. */
	grabPx: { x: number; y: number };
	/** Asset displacement at the moment of grab (so mid-flight grabs are seamless). */
	initialDisp: { x: number; y: number };
	/** Recent pointer samples for flick-velocity estimation. */
	samples: DragSample[];
	/** Element that captured the pointer (for releaseCapture on cleanup). */
	el: HTMLElement;
};

function clearShimmerSlots(
	shimmerSlots: ShimmerSlot[],
	shimmerEls: (SVGLineElement | null)[] | null | undefined,
): void {
	for (let i = 0; i < shimmerSlots.length; i++) {
		if (shimmerSlots[i] !== null) {
			shimmerSlots[i] = null;
			const el = shimmerEls?.[i];
			if (el) el.setAttribute('stroke-opacity', '0');
		}
	}
}

/**
 * Convert a client-space pointer event to stage-relative pixels.
 */
function clientToStagePx(
	clientX: number,
	clientY: number,
	rect: DOMRect,
): { x: number; y: number } {
	return { x: clientX - rect.left, y: clientY - rect.top };
}

/**
 * Drives the per-frame motion of the hero-icons stage:
 *
 *  - Stage rect tracking via `refs.stage`.
 *  - Pointer + visibility + reduced-motion listeners.
 *  - Drag + spring physics on top of the existing intro/ambient layers.
 *  - One `requestAnimationFrame` loop that writes `transform`/`opacity` to
 *    `refs.icons` entries and `x1/y1/x2/y2` to `refs.edges` entries
 *    (no React re-renders).
 *
 * The component owns the refs and wires them to its JSX. The hook only
 * reads `.current` inside effects and event listeners — never during the
 * component's render.
 */
export const useHeroMotion = (
	opts: HeroMotionOptions,
	refs: HeroMotionRefs,
	edges: readonly IconEdge[],
	assets: readonly HeroAsset[] = HERO_ASSETS,
	motionOrigin: MotionOrigin = DEFAULT_MOTION_ORIGIN,
	referenceSize: ReferenceSize = { width: REFERENCE_WIDTH, height: REFERENCE_HEIGHT },
): HeroMotionHandle => {
	const params = useMemo(() => computeAssetParams(assets, motionOrigin), [assets, motionOrigin]);

	const cfgRef = useRef<HeroMotionOptions>(opts);
	useEffect(() => {
		cfgRef.current = opts;
	});

	const reducedMotionRef = useRef(false);
	const prevNowRef = useRef(0);
	const motionNowRef = useRef(0);
	const { avatarStaggerMs, edgeGrowMs, iconAppearMs } = opts.intro;
	const introSchedule = useMemo<IntroSchedule>(
		() => computeIntroSchedule(assets, edges, { avatarStaggerMs, edgeGrowMs, iconAppearMs }),
		[assets, edges, avatarStaggerMs, edgeGrowMs, iconAppearMs],
	);
	const scheduleRef = useRef<IntroSchedule>(introSchedule);
	useEffect(() => {
		scheduleRef.current = introSchedule;
	}, [introSchedule]);

	const introStartRef = useRef(0);
	const introCleanedUpRef = useRef(false);
	const introCleanedUpAtRef = useRef(0);
	const shimmerStateRef = useRef<ShimmerSlot[]>([]);
	const nextShimmerSpawnAtRef = useRef(0);

	const shimmerPoolSize = opts.shimmer.maxConcurrent;
	useEffect(() => {
		const slots = shimmerStateRef.current;
		if (slots.length === shimmerPoolSize) return;
		if (slots.length < shimmerPoolSize) {
			while (slots.length < shimmerPoolSize) slots.push(null);
		} else {
			slots.length = shimmerPoolSize;
		}
	}, [shimmerPoolSize]);

	const replayIntro = useCallback(() => {
		const now = typeof performance !== 'undefined' ? performance.now() : 0;
		introStartRef.current = now;
		motionNowRef.current = now;
		introCleanedUpRef.current = false;
		introCleanedUpAtRef.current = 0;
		const slots = shimmerStateRef.current;
		for (let i = 0; i < slots.length; i++) slots[i] = null;
		for (const el of refs.shimmerSlots.current ?? []) {
			if (el) el.setAttribute('stroke-opacity', '0');
		}
		nextShimmerSpawnAtRef.current = 0;
	}, [refs.shimmerSlots]);

	const dynStateRef = useRef<DynStateMap>(new Map());
	const adjRef = useRef<Adjacency>({ a: [], b: [] });
	useEffect(() => {
		const fresh = createDynState(assets);
		const current = dynStateRef.current;
		for (const [id, state] of fresh) {
			if (!current.has(id)) current.set(id, state);
		}
		for (const id of current.keys()) {
			if (!fresh.has(id)) current.delete(id);
		}
		adjRef.current = buildAdjacency(current, edges);
	}, [assets, edges]);

	const dragsRef = useRef<Map<number, DragCtx>>(new Map());

	// `prefers-reduced-motion` — the rAF loop reads this each tick and skips
	// the intro / ambient drift when the user has opted in to reduced
	// motion, so the hero snaps to its resting state immediately.
	useEffect(() => {
		const win = getDocument()?.defaultView;
		if (!win?.matchMedia) return;
		const mq = win.matchMedia('(prefers-reduced-motion: reduce)');
		const update = () => {
			reducedMotionRef.current = mq.matches;
		};
		update();
		mq.addEventListener('change', update);
		return () => mq.removeEventListener('change', update);
	}, []);

	useEffect(() => {
		const doc = getDocument();
		if (!doc) return;
		let hiddenAt = 0;
		const onVisibilityChange = () => {
			if (doc.hidden) {
				hiddenAt = performance.now();
			} else if (hiddenAt > 0) {
				prevNowRef.current = performance.now();
				hiddenAt = 0;
			}
		};
		doc.addEventListener('visibilitychange', onVisibilityChange);
		return () => doc.removeEventListener('visibilitychange', onVisibilityChange);
	}, []);

	const interactive = opts.interactive ?? true;
	useEffect(() => {
		if (!interactive) return;
		const stage = refs.stage.current;
		if (!stage) return;
		// Snapshot the Map reference so the cleanup function uses the same
		// instance the listeners mutated (the ref itself never reassigns,
		// but eslint can't see that).
		const drags = dragsRef.current;

		const endDrag = (pointerId: number) => {
			const ctx = drags.get(pointerId);
			if (!ctx) return;
			const dyn = dynStateRef.current.get(ctx.id);
			if (dyn) {
				dyn.dragging = false;
				// Estimate flick velocity from the most recent samples within
				// FLICK_WINDOW_MS so a quick swipe carries momentum into the spring.
				const flick = cfgRef.current.flick;
				if (flick > 0 && ctx.samples.length >= 2) {
					const last = ctx.samples[ctx.samples.length - 1];
					// Find the oldest sample still inside the window.
					let oldest = ctx.samples[0];
					for (const s of ctx.samples) {
						if (last.t - s.t <= FLICK_WINDOW_MS) {
							oldest = s;
							break;
						}
					}
					const dtMs = last.t - oldest.t;
					if (dtMs > 0) {
						const vx = ((last.x - oldest.x) / dtMs) * 1000 * flick;
						const vy = ((last.y - oldest.y) / dtMs) * 1000 * flick;
						const speed = Math.hypot(vx, vy);
						const scale = speed > FLICK_MAX_SPEED ? FLICK_MAX_SPEED / speed : 1;
						dyn.vx = vx * scale;
						dyn.vy = vy * scale;
					}
				}
			}
			try {
				ctx.el.releasePointerCapture(pointerId);
			} catch {
				// Ignore — capture may already have been lost.
			}
			drags.delete(pointerId);
			if (drags.size === 0) {
				stage.classList.remove('hero-icons-dragging');
			}
		};

		const onPointerDown = (e: PointerEvent) => {
			if (reducedMotionRef.current) return;
			if (e.pointerType !== 'mouse' || e.button !== 0) return;
			const target = (e.target as Element | null)?.closest<HTMLElement>('[data-asset-id]');
			if (!target) return;
			const id = target.dataset.assetId;
			if (!id) return;
			const dyn = dynStateRef.current.get(id);
			if (!dyn) return;

			const rect = stage.getBoundingClientRect();
			const grab = clientToStagePx(e.clientX, e.clientY, rect);
			const ctx: DragCtx = {
				id,
				grabPx: grab,
				initialDisp: { x: dyn.dispX, y: dyn.dispY },
				samples: [{ t: performance.now(), x: grab.x, y: grab.y }],
				el: target,
			};
			drags.set(e.pointerId, ctx);
			dyn.dragging = true;
			dyn.vx = 0;
			dyn.vy = 0;
			try {
				target.setPointerCapture(e.pointerId);
			} catch {
				// Some browsers reject capture in edge cases (e.g. detached node).
			}
			stage.classList.add('hero-icons-dragging');
			if (heroLoopStoppedRef.current) {
				// The gated motion loop was stopped while frozen; a drag needs
				// frames again so the dragged icon tracks the cursor. (This ref is
				// only ever true under the perf gate, so the legacy path is a no-op.)
				setHeroLoopRestartTick((value) => value + 1);
			}
			e.preventDefault();
		};

		const onPointerMove = (e: PointerEvent) => {
			const ctx = drags.get(e.pointerId);
			if (!ctx) return;
			const dyn = dynStateRef.current.get(ctx.id);
			if (!dyn) return;
			const rect = stage.getBoundingClientRect();
			const p = clientToStagePx(e.clientX, e.clientY, rect);
			// Compose initial displacement so grabbing mid-flight is seamless.
			dyn.dispX = ctx.initialDisp.x + (p.x - ctx.grabPx.x);
			dyn.dispY = ctx.initialDisp.y + (p.y - ctx.grabPx.y);
			dyn.vx = 0;
			dyn.vy = 0;
			ctx.samples.push({ t: performance.now(), x: p.x, y: p.y });
			if (ctx.samples.length > FLICK_SAMPLE_LIMIT) ctx.samples.shift();
		};

		const onPointerEnd = (e: PointerEvent) => {
			endDrag(e.pointerId);
		};

		// Belt-and-braces: if the window loses focus (e.g. alt-tab during a
		// drag), end any in-flight drags so we don't leave nodes pinned.
		const onWindowBlur = () => {
			for (const id of [...drags.keys()]) endDrag(id);
		};

		stage.addEventListener('pointerdown', onPointerDown);
		stage.addEventListener('pointermove', onPointerMove);
		stage.addEventListener('pointerup', onPointerEnd);
		stage.addEventListener('pointercancel', onPointerEnd);
		window.addEventListener('blur', onWindowBlur);

		return () => {
			stage.removeEventListener('pointerdown', onPointerDown);
			stage.removeEventListener('pointermove', onPointerMove);
			stage.removeEventListener('pointerup', onPointerEnd);
			stage.removeEventListener('pointercancel', onPointerEnd);
			window.removeEventListener('blur', onWindowBlur);
			for (const id of [...drags.keys()]) endDrag(id);
		};
	}, [refs.stage, interactive]);

	// Perf gate: bumping this restarts the rAF effect below when motion freezes
	// or unfreezes, so the loop can be fully stopped (not just early-returning)
	// while frozen. Stays 0 forever when the gate is off → legacy deps unchanged.
	const [heroLoopRestartTick, setHeroLoopRestartTick] = useState(0);
	// True while the gated loop is stopped (frozen). A drag starting while the
	// loop is stopped must restart it so the dragged icon tracks the cursor.
	const heroLoopStoppedRef = useRef(false);

	useEffect(() => {
		let raf = 0;
		prevNowRef.current = performance.now();
		motionNowRef.current = prevNowRef.current;
		const offsets = new Map<string, { dx: number; dy: number }>();
		// Evaluated once per effect run (not per frame) — the tick reads this
		// captured value rather than calling `fg()` ~60×/sec inside the loop.
		const perfGatingEnabled = fg('twg-microsite-experience-performance-improvements');
		heroLoopStoppedRef.current = false;

		const tick = () => {
			raf = requestAnimationFrame(tick);
			if (getDocument()?.hidden) return;

			const cfg = cfgRef.current;
			const reduced = reducedMotionRef.current;
			const now = performance.now();
			const dt = (now - prevNowRef.current) / 1000;
			prevNowRef.current = now;
			if (introStartRef.current === 0) introStartRef.current = now;

			const schedule = scheduleRef.current;
			const introElapsed = now - introStartRef.current;
			const introActive = !reduced && introElapsed < schedule.totalMs;
			const introNeedsCleanup = !introCleanedUpRef.current && !introActive;
			const introWriting = introActive || introNeedsCleanup;
			const hasActiveDrag = dragsRef.current.size > 0;
			const motionFrozen = Boolean(cfg.motionFrozen) && !introWriting && !hasActiveDrag;

			if (motionFrozen) {
				clearShimmerSlots(shimmerStateRef.current, refs.shimmerSlots.current);
				nextShimmerSpawnAtRef.current = 0;
				if (perfGatingEnabled) {
					// Stop the loop entirely while frozen by cancelling the frame
					// just scheduled at the top of `tick`. The watcher effect below
					// restarts it when motion unfreezes; a drag starting while
					// stopped restarts it via `heroLoopStoppedRef` (see pointerdown).
					// The legacy path keeps waking every frame.
					cancelAnimationFrame(raf);
					raf = 0;
					heroLoopStoppedRef.current = true;
				}
				return;
			}

			const stage = refs.stage.current;
			if (!stage) return;
			const rect = stage.getBoundingClientRect();
			if (rect.width === 0 || rect.height === 0) return;
			const stageDims: StageRect = { width: rect.width, height: rect.height };

			motionNowRef.current += dt * 1000;
			const motionNow = motionNowRef.current;
			const ambientAmp = reduced ? 0 : cfg.ambientAmp;
			if (!reduced) {
				integrate(dynStateRef.current, adjRef.current, cfg.dynamics, dt);
			}

			offsets.clear();

			const iconMap = refs.icons.current;
			for (const [id, p] of params) {
				const am = computeAmbient(motionNow, p, ambientAmp);
				const dyn = dynStateRef.current.get(id);
				const dragDx = dyn ? dyn.dispX : 0;
				const dragDy = dyn ? dyn.dispY : 0;
				const dx = am.dx + dragDx;
				const dy = am.dy + dragDy;
				offsets.set(id, { dx, dy });

				const el = iconMap?.get(id);
				if (el) {
					let scale = 1;
					if (introWriting) {
						let opacity = 1;
						if (introActive) {
							const reveal = schedule.icons.get(id);
							if (reveal) {
								const t = clamp01((introElapsed - reveal.startMs) / reveal.durationMs);
								opacity = easeOutCubic(t);
								scale = easeOutBack(t, cfg.intro.iconAppearOvershoot);
							}
						}
						el.style.opacity = opacity.toFixed(3);
					}
					el.style.transform =
						scale === 1
							? `translate(-50%, -50%) translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`
							: `translate(-50%, -50%) translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px) scale(${scale.toFixed(3)})`;
				}
			}

			for (const [edgeId, ed] of refs.edges.current ?? []) {
				if (!ed.el) continue;
				const aPx = offsets.get(ed.from);
				const bPx = offsets.get(ed.to);
				const aVb = aPx ? pxToViewBox(aPx.dx, aPx.dy, stageDims, referenceSize) : { dx: 0, dy: 0 };
				const bVb = bPx ? pxToViewBox(bPx.dx, bPx.dy, stageDims, referenceSize) : { dx: 0, dy: 0 };

				const ax = ed.baseX1 + aVb.dx;
				const ay = ed.baseY1 + aVb.dy;
				const bx = ed.baseX2 + bVb.dx;
				const by = ed.baseY2 + bVb.dy;

				if (!introWriting) {
					ed.el.setAttribute('x1', ax.toFixed(2));
					ed.el.setAttribute('y1', ay.toFixed(2));
					ed.el.setAttribute('x2', bx.toFixed(2));
					ed.el.setAttribute('y2', by.toFixed(2));
					ed.liveX1 = ax;
					ed.liveY1 = ay;
					ed.liveX2 = bx;
					ed.liveY2 = by;
					continue;
				}

				const reveal = introActive ? schedule.edges.get(edgeId) : undefined;
				const eased = reveal
					? easeOutCubic(clamp01((introElapsed - reveal.startMs) / reveal.durationMs))
					: 1;
				const fromIsA = reveal ? reveal.fromAssetId === ed.from : true;

				let x1: number;
				let y1: number;
				let x2: number;
				let y2: number;
				if (fromIsA) {
					x1 = ax;
					y1 = ay;
					x2 = ax + (bx - ax) * eased;
					y2 = ay + (by - ay) * eased;
				} else {
					x1 = bx + (ax - bx) * eased;
					y1 = by + (ay - by) * eased;
					x2 = bx;
					y2 = by;
				}
				ed.el.setAttribute('x1', x1.toFixed(2));
				ed.el.setAttribute('y1', y1.toFixed(2));
				ed.el.setAttribute('x2', x2.toFixed(2));
				ed.el.setAttribute('y2', y2.toFixed(2));
				ed.el.setAttribute('stroke-opacity', (eased * cfg.edgeOpacity).toFixed(3));
				ed.liveX1 = x1;
				ed.liveY1 = y1;
				ed.liveX2 = x2;
				ed.liveY2 = y2;
			}

			const shimmerCfg = cfg.shimmer;
			const shimmerEls = refs.shimmerSlots.current;
			const shimmerSlots = shimmerStateRef.current;
			const introCompleteAt = introCleanedUpRef.current
				? introCleanedUpAtRef.current
				: introStartRef.current > 0
					? introStartRef.current + schedule.totalMs
					: Number.POSITIVE_INFINITY;
			const shimmerActive =
				now >= introCompleteAt + shimmerCfg.startOffsetMs &&
				!reduced &&
				shimmerCfg.enabled &&
				shimmerCfg.brightness > 0 &&
				shimmerEls !== null;

			if (shimmerActive && shimmerEls) {
				if (nextShimmerSpawnAtRef.current === 0) {
					nextShimmerSpawnAtRef.current =
						now + nextPoissonInterval(shimmerCfg.avgIntervalMs, Math.random);
				}
				if (now >= nextShimmerSpawnAtRef.current) {
					let freeSlot = -1;
					for (let i = 0; i < shimmerSlots.length; i++) {
						if (shimmerSlots[i] === null && shimmerEls[i]) {
							freeSlot = i;
							break;
						}
					}
					if (freeSlot !== -1 && refs.edges.current.size > 0) {
						const edgeId = pickRandomEdgeId(Array.from(refs.edges.current.keys()), Math.random);
						if (edgeId !== undefined) {
							shimmerSlots[freeSlot] = {
								edgeId,
								startMs: now,
								durationMs: shimmerCfg.durationMs,
								pulseLen: shimmerCfg.pulseLen,
							};
						}
					}
					nextShimmerSpawnAtRef.current =
						now + nextPoissonInterval(shimmerCfg.avgIntervalMs, Math.random);
				}

				const shimmerGrads = refs.shimmerGrads.current;
				for (let i = 0; i < shimmerSlots.length; i++) {
					const slot = shimmerSlots[i];
					const el = shimmerEls[i];
					if (!el || !slot) continue;
					const ed = refs.edges.current.get(slot.edgeId);
					if (!ed) {
						shimmerSlots[i] = null;
						el.setAttribute('stroke-opacity', '0');
						continue;
					}
					const t = (now - slot.startMs) / slot.durationMs;
					if (t >= 1) {
						shimmerSlots[i] = null;
						el.setAttribute('stroke-opacity', '0');
						continue;
					}
					const eased = easeInOutSine(t);
					const lead = -slot.pulseLen + eased * (1 + 2 * slot.pulseLen);
					const trail = lead - slot.pulseLen;
					const dx = ed.liveX2 - ed.liveX1;
					const dy = ed.liveY2 - ed.liveY1;

					el.setAttribute('x1', ed.liveX1.toFixed(2));
					el.setAttribute('y1', ed.liveY1.toFixed(2));
					el.setAttribute('x2', ed.liveX2.toFixed(2));
					el.setAttribute('y2', ed.liveY2.toFixed(2));
					el.setAttribute('stroke-opacity', shimmerCfg.brightness.toFixed(3));

					const grad = shimmerGrads?.[i];
					if (grad) {
						grad.setAttribute('x1', (ed.liveX1 + dx * trail).toFixed(2));
						grad.setAttribute('y1', (ed.liveY1 + dy * trail).toFixed(2));
						grad.setAttribute('x2', (ed.liveX1 + dx * lead).toFixed(2));
						grad.setAttribute('y2', (ed.liveY1 + dy * lead).toFixed(2));
					}
				}
			} else {
				clearShimmerSlots(shimmerSlots, shimmerEls);
				nextShimmerSpawnAtRef.current = 0;
			}

			if (introNeedsCleanup) {
				introCleanedUpRef.current = true;
				introCleanedUpAtRef.current = now;
				cfg.onIntroComplete?.();
			}
		};

		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [
		params,
		referenceSize,
		refs.stage,
		refs.icons,
		refs.edges,
		refs.shimmerSlots,
		refs.shimmerGrads,
		heroLoopRestartTick,
	]);

	// Perf gate: the rAF loop stops itself while frozen (see the frozen branch
	// above), so it must be explicitly restarted when motion unfreezes. Bumping
	// `heroLoopRestartTick` re-runs the effect above, which tears down the (idle)
	// loop and starts a fresh one. No-op when the gate is off.
	const motionFrozenMountedRef = useRef(false);
	useEffect(() => {
		if (!fg('twg-microsite-experience-performance-improvements')) {
			return;
		}
		if (!motionFrozenMountedRef.current) {
			motionFrozenMountedRef.current = true;
			return;
		}
		setHeroLoopRestartTick((value) => value + 1);
	}, [opts.motionFrozen]);

	return { replayIntro };
};
